import Progress from 'antd/lib/progress'
import BigNumber from 'bignumber.js'
import { now } from 'lodash'
import styled from 'styled-components'
import { claimModalVar } from '../../../graphql/variables/SharedVariable'
import { formatToLocaleString, toHumanFormat } from '../../../services/UtilService'
import { WalletERC20Share } from '../../../types/WalletTypes'
import CardBase from '../../design-system/CardBase'
import Icon, { CardTypes } from '../Icon'

export interface FractionsCardProps {
  erc20: WalletERC20Share
  chainId: number
}

export default function FractionsCard({ erc20, chainId }: FractionsCardProps) {
  const actions: {
    id: string
    name: string

    action: () => void
  }[] = []
  if (erc20.released || (erc20.cutoff && now() / 1000 > erc20.cutoff)) {
    actions.push({
      id: 'claim',
      name: 'Claim',
      action: () => claimModalVar(erc20)
    })
  }
  const calculatePercent = new BigNumber(erc20.balance).dividedBy(erc20.totalSupply).multipliedBy(100).toNumber()
  return (
    <CardBase
      urlRedirect={`/marketplace/${chainId}/detail/${erc20.id}`}
      totalNfts={erc20.nftsCount || 1}
      network={erc20.chainId || 1}
      animationUrl={erc20.metadata?.animation_url}
      imageUrl={String(erc20.metadata?.image)}
      actions={actions}
    >
      <Footer>
        <Info>
          <h4>{`${erc20.name || erc20?.metadata?.name}`}</h4>
          <div>
            <span>Balance: </span>
            <span>
              {toHumanFormat(Number(erc20.balance))}
              <small>{erc20.symbol}</small>
            </span>
          </div>
        </Info>
        <StatusBar>
          <div>
            <Icon name={CardTypes.ownership} size={18} />
            <span>Ownership</span>
          </div>
          <ProgressBar>
            <Progress
              showInfo
              percent={Number(formatToLocaleString(calculatePercent, 2, 0))}
              format={() => `${Number(formatToLocaleString(calculatePercent, 2, 0))}%`}
            />
          </ProgressBar>
        </StatusBar>
      </Footer>
    </CardBase>
  )
}

const { Footer, Info, StatusBar, ProgressBar } = {
  Footer: styled.div`
    background: ${props => props.theme.colors.gray[1]};
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  `,
  Info: styled.div`
    padding: 8px 16px;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 28px 24px;

    > h4 {
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: normal;
      color: ${props => props.theme.colors.black};
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
    > div {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 8px;
      > span {
        font-weight: 400;
        font-size: 14px;
        color: ${props => props.theme.colors.black};
        small {
          margin-left: 5px;
          font-weight: normal;
          font-size: 12px;
        }
      }
    }
  `,
  StatusBar: styled.div`
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    padding: 0px 16px;
    height: 56px;
    background: ${props => props.theme.colors.gray[0]};
    display: grid;
    grid-template-columns: 108px 1fr;
    column-gap: 8px;
    align-items: center;

    div:nth-child(1) {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      > span {
        font-size: 14px;
        color: ${props => props.theme.colors.black};
        margin-left: 8px;
      }
    }
  `,
  ProgressBar: styled.div`
    width: 100%;
    justify-self: end;
    .ant-progress-outer {
      display: flex;
      align-items: center;
      width: 100px;
    }
    .ant-progress.ant-progress-line.ant-progress-status-normal.ant-progress-show-info.ant-progress-default {
      justify-content: end;
    }
    .ant-progress.ant-progress-line.ant-progress-status-normal.ant-progress-show-info.ant-progress-default {
      span {
        font-style: normal;
        font-weight: 500;
        font-size: 13px;
        line-height: 33px;
        margin-left: 8px;
        margin-right: 8px;
      }
    }

    .ant-progress-inner {
      background: ${props => props.theme.colors.white};
      border: 1px solid ${props => props.theme.colors.gray[0]};
    }

    .ant-progress-bg {
      height: 8px !important;
      background: linear-gradient(90deg, #fe8367 5.73%, #fe7688 100%);
    }
  `
}
